// Self-serve registration page reached from an approval email.
//
// URL: /register?token=<rawInvitationToken>
//
// Spec: docs/spec/core/part2-product-requirements.md §13.5 (Reqs 594-599)
// Spec: docs/spec/topics/onboarding/mfa-enrollment.md (TOTP path)
//
// Three-step wizard:
//   1. credentials — name + password ⇒ trpc.auth.acceptInvitation
//   2. mfa-enroll  — TOTP QR + 6-digit verify ⇒ startMfaEnrollment + completeMfaEnrollment
//   3. recovery    — show the 10 single-use recovery codes
// "Continue to Cade" then hydrates the AuthProvider (localStorage + state)
// and routes to '/'.
//
// MFA scope here is TOTP-only; passkey UI is a follow-up. The auth-service
// procedures already support both — the gateway proxy accepts either
// method.

import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { trpc } from '../api/trpc';
import { useAuth } from '../hooks/useAuth';
import { CadeAvatar } from '../components/CadeAvatar';

type Step = 'credentials' | 'mfa-enroll' | 'recovery';

interface RegistrationSession {
  token: string;
  userId: string;
  tenantId: string;
  role: string;
}

export function RegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { hydrate } = useAuth();
  const inviteToken = params.get('token') ?? '';

  const [step, setStep] = useState<Step>('credentials');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [session, setSession] = useState<RegistrationSession | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [base32Secret, setBase32Secret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const accept = trpc.auth.acceptInvitation.useMutation();
  const startMfa = trpc.auth.startMfaEnrollment.useMutation();
  const completeMfa = trpc.auth.completeMfaEnrollment.useMutation();

  // Render the QR code whenever a new otpauth URI lands.
  useEffect(() => {
    if (!otpauthUri) { setQrDataUrl(null); return; }
    let cancelled = false;
    QRCode.toDataURL(otpauthUri, { width: 200, margin: 1 })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(null); });
    return () => { cancelled = true; };
  }, [otpauthUri]);

  const missingToken = !inviteToken;

  async function handleCredentialsSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }
    try {
      const result = await accept.mutateAsync({
        token: inviteToken,
        displayName: displayName.trim(),
        password,
      });
      setSession(result);
      // Kick off MFA enrollment immediately so the QR is ready when we render step 2.
      const enroll = await startMfa.mutateAsync({
        sessionToken: result.token,
        method: 'totp',
      });
      const totpEnroll = enroll as { otpauthUri: string; base32Secret: string };
      setOtpauthUri(totpEnroll.otpauthUri);
      setBase32Secret(totpEnroll.base32Secret);
      setStep('mfa-enroll');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed.');
    }
  }

  async function handleMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!session) {
      setError('Registration session lost — refresh and try again.');
      return;
    }
    const code = totpCode.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    try {
      const result = await completeMfa.mutateAsync({
        sessionToken: session.token,
        method: 'totp',
        response: { code },
      });
      const mfaResult = result as { methodId: string; recoveryCodes: string[] | null };
      setRecoveryCodes(mfaResult.recoveryCodes ?? []);
      setStep('recovery');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    }
  }

  function handleContinueToChat() {
    if (!session) { return; }
    hydrate({ token: session.token, userId: session.userId, tenantId: session.tenantId });
    navigate('/', { replace: true });
  }

  const submitting = accept.isPending || startMfa.isPending;
  const verifying = completeMfa.isPending;

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <CadeAvatar size={80} />
          <span className="login-logo-text">Welcome to Cade</span>
        </div>

        {missingToken && (
          <p className="login-error" data-testid="register-missing-token">
            This registration link is missing or invalid. Re-open the link from your invitation email,
            or request access at <a href="https://getcade.ai/request-access/index.html">getcade.ai</a>.
          </p>
        )}

        {!missingToken && step === 'credentials' && (
          <form className="login-form" onSubmit={(e) => { void handleCredentialsSubmit(e); }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted, #888)' }}>
              Set your name and a password to finish creating your Cade account.
            </p>
            <div className="form-field">
              <label htmlFor="register-name">Your name</label>
              <input
                id="register-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                autoComplete="name"
                placeholder="Alex Doe"
              />
            </div>
            <div className="form-field">
              <label htmlFor="register-password">Password (12+ characters)</label>
              <input
                id="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="••••••••••••"
              />
            </div>
            <div className="form-field">
              <label htmlFor="register-password-confirm">Confirm password</label>
              <input
                id="register-password-confirm"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="••••••••••••"
              />
            </div>
            {error && <p className="login-error" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={submitting} className="btn-recommended" data-testid="register-credentials-submit">
              {submitting ? <><span className="btn-spinner" aria-hidden="true" data-testid="register-credentials-spinner" />Creating your account…</> : 'Continue'}
            </button>
          </form>
        )}

        {!missingToken && step === 'mfa-enroll' && (
          <form className="login-form" onSubmit={(e) => { void handleMfaSubmit(e); }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted, #888)' }}>
              Scan this QR code in your authenticator app (1Password, Authy, Google Authenticator),
              then enter the 6-digit code it shows.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="TOTP setup QR code"
                  data-testid="register-totp-qr"
                  style={{ width: 200, height: 200, background: '#fff', padding: 8, borderRadius: 6 }}
                />
              ) : (
                <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted, #888)' }}>
                  Loading QR…
                </div>
              )}
            </div>
            {base32Secret && (
              <details style={{ fontSize: 12, color: 'var(--color-text-muted, #888)' }}>
                <summary>Can't scan? Enter this secret manually</summary>
                <code
                  data-testid="register-totp-secret"
                  style={{ display: 'block', marginTop: 6, wordBreak: 'break-all', fontFamily: 'monospace' }}
                >
                  {base32Secret}
                </code>
              </details>
            )}
            <div className="form-field">
              <label htmlFor="register-totp-code">Code</label>
              <input
                id="register-totp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                required
                autoComplete="one-time-code"
                placeholder="123456"
              />
            </div>
            {error && <p className="login-error" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={verifying} className="btn-recommended" data-testid="register-mfa-submit">
              {verifying ? <><span className="btn-spinner" aria-hidden="true" data-testid="register-mfa-spinner" />Verifying your code…</> : 'Verify and finish'}
            </button>
          </form>
        )}

        {!missingToken && step === 'recovery' && (
          <div className="login-form">
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted, #888)' }}>
              Save these recovery codes somewhere safe. Each one can be used once to sign in if you
              lose access to your authenticator app.
            </p>
            <RecoveryCodeList codes={recoveryCodes ?? []} />
            <button type="button" className="btn-recommended" onClick={handleContinueToChat} data-testid="register-continue-to-chat">
              Continue to Cade
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RecoveryCodeList({ codes }: { codes: string[] }) {
  const joined = useMemo(() => codes.join('\n'), [codes]);
  return (
    <div data-testid="register-recovery-codes">
      <pre
        style={{
          background: 'var(--color-bg-input, #0a0a0a)',
          color: 'inherit',
          padding: 12,
          borderRadius: 6,
          fontFamily: 'monospace',
          fontSize: 13,
          margin: 0,
        }}
      >
{joined}
      </pre>
      <button
        type="button"
        className="btn-link"
        style={{ marginTop: 8 }}
        onClick={() => { void navigator.clipboard?.writeText(joined); }}
      >
        Copy all codes
      </button>
    </div>
  );
}
